import { ObjectSchema, Schema, boolean, number, object, string } from 'yup';
import { AlarmSettingValidationErrors } from './../validation-errors/alarm-settings';
import { Channel, ChannelFns } from './../../models/channel';
import { AlarmLevel } from './../../models/alarm-level';
import { AlarmSetting } from './../../models/alarm-setting';
import * as yup from 'yup';
import { locale } from './../yup-locale';
yup.setLocale(locale);

const alarmLevels: number[] = Object.values(AlarmLevel).map(v => Number(v));

export const contactStatusMaxLength = 10;

function requiredWhenEnabled<T extends Schema>(schema: T, disabledSchema: T): T {
    return schema.when('enable', {
        is: true,
        then: s => s.required(),
        otherwise: s => disabledSchema.nullable().optional(),
    });
}

export enum AlarmSettingValidationField {
    ALARM_VERY_LOW,
    ALARM_LOW,
    ALARM_HIGH,
    ALARM_VERY_HIGH,
    ALARM_SETTING,
    ALARM_SETTING_FEEDBACK_ERROR,
    ALARM_SETTING_SENSOR_FAIL,
}

export function alarmSettingValidator(
    channel: Channel,
    alarmOption: AlarmSettingValidationField
): ObjectSchema<AlarmSetting> {
    function testRange(value) {
        let lowerValue = channel.fcuData.measurableRange.min - 1;
        let upperValue = channel.fcuData.measurableRange.max + 1;
        let lowerValueErrorString = AlarmSettingValidationErrors.NOT_WITHIN_MEASURABLE_RANGE;
        let upperValueErrorString = AlarmSettingValidationErrors.NOT_WITHIN_MEASURABLE_RANGE;

        if (value < channel.fcuData.measurableRange.min) {
            return this.createError({
                message: AlarmSettingValidationErrors.NOT_WITHIN_MEASURABLE_RANGE,
                params: {},
            });
        } else if (value > channel.fcuData.measurableRange.max) {
            return this.createError({
                message: AlarmSettingValidationErrors.NOT_WITHIN_MEASURABLE_RANGE,
                params: {},
            });
        }

        if (alarmOption == AlarmSettingValidationField.ALARM_VERY_LOW) {
            if (channel.fcuData.alarmSettingLow.enable) {
                upperValue = channel.fcuData.alarmSettingLow.setPoint;
                upperValueErrorString = AlarmSettingValidationErrors.NOT_BELOW_LOW_ALARM;
            } else {
                upperValue = channel.fcuData.normalRange.min;
                upperValueErrorString = AlarmSettingValidationErrors.NOT_BELOW_NORMAL_RANGE;
            }
        } else if (alarmOption == AlarmSettingValidationField.ALARM_LOW) {
            if (channel.fcuData.alarmSettingVeryLow.enable) {
                lowerValue = channel.fcuData.alarmSettingVeryLow.setPoint;
                lowerValueErrorString + AlarmSettingValidationErrors.NOT_ABOVE_VERY_LOW_ALARM;
            }
            upperValue = channel.fcuData.normalRange.min;
            upperValueErrorString = AlarmSettingValidationErrors.NOT_BELOW_NORMAL_RANGE;
        } else if (alarmOption == AlarmSettingValidationField.ALARM_HIGH) {
            if (channel.fcuData.alarmSettingVeryHigh.enable) {
                upperValue = channel.fcuData.alarmSettingVeryHigh.setPoint;
                upperValueErrorString = AlarmSettingValidationErrors.NOT_BELOW_VERY_HIGH_ALARM;
            }

            lowerValue = channel.fcuData.normalRange.max;
            lowerValueErrorString = AlarmSettingValidationErrors.NOT_ABOVE_NORMAL_RANGE;
        } else if (alarmOption == AlarmSettingValidationField.ALARM_VERY_HIGH) {
            if (channel.fcuData.alarmSettingHigh.enable) {
                lowerValue = channel.fcuData.alarmSettingHigh.setPoint;
                lowerValueErrorString = AlarmSettingValidationErrors.NOT_ABOVE_HIGH_ALARM;
            } else {
                lowerValue = channel.fcuData.normalRange.max;
                lowerValueErrorString = AlarmSettingValidationErrors.NOT_ABOVE_NORMAL_RANGE;
            }
        } else if (alarmOption == AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR) {
            //Feedback error doesn't check the set point
            lowerValue = value - 1;
            upperValue = value + 1;
        }

        if (value <= lowerValue) {
            return this.createError({ message: lowerValueErrorString, params: {} });
        } else if (value >= upperValue) {
            return this.createError({ message: upperValueErrorString, params: {} });
        } else {
            return true;
        }
    }

    let digitalStringConditional = (enable, schema: Schema) => {
        return enable && ChannelFns.isDigital(channel) ? schema.required() : schema.nullable().optional();
    };

    let setPointValidator =
        alarmOption !== AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR
            ? number().required().integer().test('setPoint range', testRange)
            : number()
                  .required()
                  .transform(v => Number(v));

    let schema = {
        enable: boolean().required(),
        statusString: string().nullable().optional(),
        statusStringContactOn: string().max(contactStatusMaxLength).when('enable', digitalStringConditional),
        statusStringContactOff: string().max(contactStatusMaxLength).when('enable', digitalStringConditional),
        setPoint: setPointValidator,
        alarmGroupNo: number().integer().min(0),
        reposeGroup1No: number().integer().min(0),
        reposeGroup2No: number().integer().min(0),
        manualReposeFlag: boolean(),
        delayTimer: number().integer().min(0),
        normalOpenFlag: boolean().nullable().optional(),
        alarmLevel: number().oneOf(alarmLevels),
    };

    return object(schema);
}
